import { DateTime } from 'luxon'
import {
  BorderConnectCommodity,
  BorderConnectParty,
  BorderConnectSendPayload,
  BorderConnectShipment,
  BorderConnectTrip,
} from 'store/borderconnect/borderconnect.types'
import {
  AceCommodity,
  AceParty,
  AceShipment,
  AceTripFormValues,
  ManifestKind,
  defaultAceTripFormValues,
  emptyShipment,
  toAceWeightUnit,
} from './ace-trip-form'

const toBorderConnectDateTime = (value: string) => {
  if (!value) return ''
  const iso = DateTime.fromISO(value)
  if (iso.isValid) return iso.toFormat('yyyy-MM-dd HH:mm:ss')
  return value.replace('T', ' ').slice(0, 19)
}

const toNumber = (value: string) => {
  if (!value?.trim()) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : value
}

export type BorderConnectOperation = 'CREATE' | 'UPDATE'

const toDriverNumber = (values: AceTripFormValues) => {
  const stored = values.driverNumber?.trim()
  if (stored) return stored
  const fromName = `${values.driverLastName}${values.driverFirstName}`
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 4)
    .toUpperCase()
  return fromName || 'D001'
}

const toShipment = (
  shipment: AceShipment,
  isAce: boolean,
  trailerNumber: string,
  operation: BorderConnectOperation
): BorderConnectShipment => {
  const commodity = shipment.commodities[0]
  const loadedOnNumber = commodity?.loadedOn?.trim() || trailerNumber
  return {
    data: isAce ? 'ACE_SHIPMENT' : 'ACI_SHIPMENT',
    operation,
    type: shipment.shipmentType,
    shipmentControlNumber: shipment.shipmentControlNumber,
    provinceOfLoading: shipment.provinceOfLoading,
    shipper: {
      name: shipment.shipper.name,
      address: {
        addressLine: shipment.shipper.address,
        city: shipment.shipper.city,
        stateProvince: shipment.shipper.stateProvince,
        postalCode: shipment.shipper.postalCode,
      },
    },
    consignee: {
      name: shipment.consignee.name,
      address: {
        addressLine: shipment.consignee.address,
        city: shipment.consignee.city,
        stateProvince: shipment.consignee.stateProvince,
        postalCode: shipment.consignee.postalCode,
      },
    },
    commodities: [
      {
        loadedOn: loadedOnNumber
          ? { type: 'TRAILER', number: loadedOnNumber }
          : undefined,
        description: commodity?.description,
        quantity: toNumber(commodity?.quantity ?? ''),
        packagingUnit: commodity?.packagingUnit,
        weight: toNumber(commodity?.weight ?? ''),
        weightUnit: commodity?.weightUnit,
      },
    ],
  }
}

export const buildBorderConnectSendPayload = (
  userId: string,
  values: AceTripFormValues,
  operation: BorderConnectOperation = 'CREATE'
): BorderConnectSendPayload => {
  const isAce = values.manifestKind === 'ACE'
  const trailerNumber = values.trailer.trailerNumber.trim()
  const payload: BorderConnectSendPayload = {
    PK: userId,
    data: isAce ? 'ACE_TRIP' : 'ACI_TRIP',
    operation,
    tripNumber: values.tripNumber.trim(),
    estimatedArrivalDateTime: toBorderConnectDateTime(values.estimatedArrival),
    autoSend: values.autoSend ?? true,
    truck: {
      number: values.truckNumber,
      type: values.truckType,
      vinNumber: values.vinNumber,
      licensePlate: {
        number: values.truckLicensePlateNumber,
        stateProvince: values.truckLicensePlateState,
      },
    },
    trailers: trailerNumber
      ? [
          {
            number: values.trailer.trailerNumber,
            type: values.trailer.trailerType,
            licensePlate: {
              number: values.trailer.licensePlateNumber,
              stateProvince: values.trailer.licensePlateState,
            },
          },
        ]
      : [],
    drivers: [
      {
        driverNumber: toDriverNumber(values),
        firstName: values.driverFirstName,
        lastName: values.driverLastName,
        gender: values.gender,
        dateOfBirth: values.dateOfBirth,
        citizenshipCountry: values.citizenshipCountry,
        ...(values.fastCardNumber.trim()
          ? { fastCardNumber: values.fastCardNumber.trim() }
          : {}),
      },
    ],
    shipments: values.shipments.map((shipment) =>
      toShipment(shipment, isAce, trailerNumber, operation)
    ),
  }

  if (isAce) {
    payload.usPortOfArrival = values.usPortOfArrival
  } else {
    payload.portOfArrival = values.usPortOfArrival
  }

  return payload
}

const toDateTimeLocal = (value?: string) => {
  if (!value) return ''
  const iso = DateTime.fromISO(value.replace(' ', 'T'))
  if (iso.isValid) return iso.toFormat("yyyy-MM-dd'T'HH:mm")
  const formatted = DateTime.fromFormat(value, 'yyyy-MM-dd HH:mm:ss')
  if (formatted.isValid) return formatted.toFormat("yyyy-MM-dd'T'HH:mm")
  return value.includes('T') ? value.slice(0, 16) : ''
}

const toText = (value?: string | number | null) =>
  value === undefined || value === null ? '' : String(value)

const toParty = (party?: BorderConnectParty): AceParty => ({
  name: party?.name ?? '',
  address: party?.address?.addressLine ?? '',
  city: party?.address?.city ?? '',
  stateProvince: party?.address?.stateProvince ?? '',
  postalCode: party?.address?.postalCode ?? '',
})

const toCommodity = (commodity?: BorderConnectCommodity): AceCommodity => ({
  description: commodity?.description ?? '',
  quantity: toText(commodity?.quantity),
  packagingUnit: commodity?.packagingUnit ?? '',
  weight: toText(commodity?.weight),
  weightUnit: toAceWeightUnit(commodity?.weightUnit) || 'L',
  loadedOn: commodity?.loadedOn?.number ?? '',
})

const storedTripKind = (trip: BorderConnectTrip): ManifestKind => {
  const sk = trip.SK?.toUpperCase() ?? ''
  if (sk.startsWith('ACI#')) return 'ACI'
  if (sk.startsWith('ACE#')) return 'ACE'
  return String(trip.data ?? '').toUpperCase().includes('ACI') ? 'ACI' : 'ACE'
}

export const storedTripToAceFormValues = (
  trip: BorderConnectTrip
): AceTripFormValues => {
  const driver = trip.drivers?.[0]
  const trailer = trip.trailers?.[0]
  const shipments: AceShipment[] = (trip.shipments ?? []).map((shipment) => ({
    shipmentType: shipment.type ?? '',
    shipmentControlNumber: shipment.shipmentControlNumber ?? '',
    provinceOfLoading: shipment.provinceOfLoading ?? '',
    shipper: toParty(shipment.shipper),
    consignee: toParty(shipment.consignee),
    commodities: (shipment.commodities?.length
      ? shipment.commodities
      : [{}]
    ).map(toCommodity),
  }))

  return {
    ...defaultAceTripFormValues,
    manifestKind: storedTripKind(trip),
    tripNumber: trip.tripNumber ?? '',
    usPortOfArrival: trip.usPortOfArrival || trip.portOfArrival || '',
    estimatedArrival: toDateTimeLocal(trip.estimatedArrivalDateTime),
    truckNumber: trip.truck?.number ?? '',
    truckType: trip.truck?.type ?? '',
    vinNumber: trip.truck?.vinNumber ?? '',
    truckLicensePlateNumber: trip.truck?.licensePlate?.number ?? '',
    truckLicensePlateState: trip.truck?.licensePlate?.stateProvince ?? '',
    trailer: {
      trailerNumber: trailer?.number ?? '',
      trailerType: trailer?.type ?? '',
      licensePlateNumber: trailer?.licensePlate?.number ?? '',
      licensePlateState: trailer?.licensePlate?.stateProvince ?? '',
    },
    driverNumber: driver?.driverNumber ?? '',
    driverFirstName: driver?.firstName ?? '',
    driverLastName: driver?.lastName ?? '',
    gender: driver?.gender ?? '',
    dateOfBirth: driver?.dateOfBirth ?? '',
    citizenshipCountry: driver?.citizenshipCountry ?? '',
    fastCardNumber: driver?.fastCardNumber ?? '',
    autoSend: trip.autoSend ?? true,
    shipments: shipments.length > 0 ? shipments : [emptyShipment()],
  }
}
